import React, { useState } from 'react';
import { ClipLoader } from 'react-spinners'; // react-spinners パッケージからのインポート
import { getMediaTypeInfo, isVideo } from "../../utils";

// mui
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';

// type
type MediaCardProps = {
    id: number | undefined,
    url: string|null,
    name: string,
    size: string,
    uploadedDate: string,
    uploading: boolean,
    handleDeleteMedia: (mediaId: number) => void,
}

export default function MediaCard(props: MediaCardProps) {
    const { id, url, name, size, uploadedDate, uploading, handleDeleteMedia } = props;

    const copyToClipboard = () => {
        url && navigator.clipboard.writeText(url)
            .then(() => {
                console.log('File url copied to clipboard');
            })
            .catch((err) => {
                console.error('Error copying to clipboard:', err);
            });

        setIsOpenSnackbar(true);
        setTextOnSnackbar((isVideoFile ? '動画' : '画像') + 'をコピーしました。');
    };

    const title = (<>
        <Chip
            label={getMediaTypeInfo(name, "typeJa")}
            variant="outlined"
            sx={{
                mr: 1,
                borderColor: getMediaTypeInfo(name, "color"),
                color: getMediaTypeInfo(name, "color"),
            }}
        />
        {name}
    </>);

    const information = (<>
        <Typography
            component="span"
            gutterBottom
            paragraph
            sx={{ display: "block", mb: 1, lineHeight: 1 }}
        >
            アップロード：{uploadedDate}
        </Typography>
        <Typography
            component="span"
            gutterBottom
            paragraph
            sx={{ display: "block", mb: 0, lineHeight: 1 }}
        >
            サイズ：{size}
        </Typography>
    </>);

    // isVideo 関数を使用して動画かどうかを判断
    const isVideoFile = isVideo(name);

    const action = (<>
        {url && id &&
            <Button
                variant="outlined"
                color="error"
                sx={{
                    mr: 3,
                    backgroundColor: (theme) => theme.palette.common.white,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMedia(id);
                }}
            >削除</Button>
        }

        {uploading && <ClipLoader size={20} color={'#36D7B7'} loading={true} />}
        {url &&
            <Button
                variant="outlined"
                color="inherit"
                sx={{
                    backgroundColor: (theme) => theme.palette.common.white,
                }}
                onClick={copyToClipboard}
            >{isVideoFile ? '動画' : '画像'}コピー</Button>
        }
    </>);

    /**
     * スナックバー
     */
    const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
    const [textOnSnackbar, setTextOnSnackbar] = useState('');

    const handleCloseSnackbar = () => {
        setIsOpenSnackbar(false);
        setTextOnSnackbar('');
    }

    return (
        <Card sx={{
            width: "calc(100% - 16px)",
            maxWidth: 980,
            mx: "auto",
            '& > a': {
                color: (theme) => theme.palette.grey[900],
                textDecoration: "none",
            }
        }}>
            <CardHeader
                avatar={
                    <Avatar sx={{}} variant="square" aria-label="room-thumbnail">
                        {url &&
                            <CardMedia
                                component={isVideoFile ? 'video' : 'img'}
                                controls={isVideoFile}  // 動画の場合のみコントロールを有効にする
                                image={url}
                                alt={isVideoFile ? '動画サムネイル' : '画像サムネイル'}
                            />
                        }
                    </Avatar>
                }
                action={action}
                title={title}
                subheader={information}
                sx={{
                    flexWrap: {mb: "wrap", tb: "nowrap"},
                    alignItems: { mb: "start", tb: "center" },
                    px: { mb: 2, tb: 3 },
                    '& .MuiCardHeader-avatar': {
                        width: { mb: 64, tb: "unset"},
                        mr: { mb: 1, tb: 2 },
                        '& .MuiAvatar-root': {
                            width: { mb: 64, tb: 80 },
                            height: { mb: 64, tb: 80 },
                        }
                    },
                    '& .MuiCardHeader-content': {
                        width: { mb: "calc(100% - 88px)", tb: "unset" },
                        marginBottom: { mb: 2, tb: 0 },
                        '& .MuiCardHeader-title': {
                            marginBottom: 1,
                            '& .MuiTypography-root': {
                                fontSize: 16,
                                fontWeight: 700,
                            }
                        },
                        '& .MuiCardHeader-subheader': {
                            '& .MuiTypography-root': {
                                fontSize: 14,
                            }
                        }
                    },
                    '& .MuiCardHeader-action': {
                        alignSelf: "inherit",
                        mx: { mb: "auto", tb: 0 },
                        my: 0,
                    }
                }}
            />
            {/* スナックバー */}
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={isOpenSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {textOnSnackbar}
                </Alert>
            </Snackbar>
        </Card>
    );
}
